import React, { useState, useEffect } from 'react';
import { 
  DollarSign, 
  Search,
  Download,
  CheckCircle,
  Clock,
  XCircle,
  Filter,
  Calendar,
  TrendingUp,
  CreditCard,
  Smartphone,
  ArrowUpRight,
  Activity
} from 'lucide-react';
import { AdminLayout } from '../../components/Admin/AdminLayout';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/authStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

interface Transaction {
  id: string;
  orderNo: string;
  userId: string;
  userName: string;
  userEmail: string;
  amount: number;
  currency: string;
  channel: string;
  status: 'success' | 'pending' | 'failed';
  subscriptionPlan: string;
  createdAt: string;
  paidAt?: string;
}

interface FinancialSummary {
  totalRevenue: number;
  monthlyRevenue: number;
  totalPayments: number;
  successfulPayments: number;
  pendingPayments: number;
  failedPayments: number;
  averageTransactionValue: number;
}

interface BillingQueueIntent {
  id: string;
  status: string;
  product_type: string;
  product_id: string;
  amount: number;
  currency: string;
  created_at: string;
  invoice?: {
    id: string;
    status: string;
    total_amount: number;
    currency: string;
    line_items?: Array<{ name?: string; product_type?: string; product_id?: string }>;
  };
  user?: {
    email?: string;
    name?: string;
    subscription?: string;
  };
}

export const FinancialReports: React.FC = () => {
  const token = useAuthStore((state) => state.token);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [summary, setSummary] = useState<FinancialSummary | null>(null);
  const [billingQueue, setBillingQueue] = useState<BillingQueueIntent[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'success' | 'pending' | 'failed'>('all');

  useEffect(() => {
    fetchFinancialData();
  }, []);

  const fetchFinancialData = async () => {
    try {
      if (!token) return;
      
      const [summaryRes, paymentsRes, billingQueueRes] = await Promise.all([
        fetch(`${API_URL}/admin/financial/summary`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API_URL}/admin/financial/payments`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API_URL}/admin/billing/intents?status=open`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
      ]);

      if (!summaryRes.ok || !paymentsRes.ok) {
        throw new Error('Failed to fetch financial data');
      }

      const summaryData = await summaryRes.json();
      const paymentsData = await paymentsRes.json();
      const billingQueueData = billingQueueRes.ok ? await billingQueueRes.json() : { intents: [] };

      setSummary(summaryData);
      setTransactions(paymentsData);
      setBillingQueue(billingQueueData.intents || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching financial data:', error);
      toast.error('Failed to load financial data');
      setLoading(false);
    }
  };

  const filteredTransactions = transactions.filter(txn => {
    const matchesSearch = 
      txn.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      txn.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      txn.orderNo.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || txn.status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  const handleBillingAction = async (intent: BillingQueueIntent, action: 'mark-paid' | 'mark-failed') => {
    const invoiceId = intent.invoice?.id;
    if (!invoiceId) {
      toast.error('Missing invoice for this billing intent');
      return;
    }

    if (action === 'mark-paid') {
      const confirmed = window.confirm('Mark this invoice paid and activate the subscription/add-on?');
      if (!confirmed) return;
    }

    try {
      const response = await fetch(`${API_URL}/admin/billing/invoices/${invoiceId}/${action}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(action === 'mark-failed' ? { reason: 'Manual payment confirmation failed' } : {}),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Billing action failed');
      }

      toast.success(action === 'mark-paid' ? 'Invoice marked paid' : 'Invoice marked failed');
      await fetchFinancialData();
    } catch (error) {
      console.error('Billing action failed:', error);
      toast.error(error instanceof Error ? error.message : 'Billing action failed');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Clock className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <span className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-semibold">Success</span>;
      case 'pending':
        return <span className="px-3 py-1 bg-yellow-50 text-yellow-700 rounded-full text-xs font-semibold">Pending</span>;
      case 'failed':
        return <span className="px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-semibold">Failed</span>;
      default:
        return <span className="px-3 py-1 bg-gray-50 text-gray-700 rounded-full text-xs font-semibold">Unknown</span>;
    }
  };

  const getPaymentMethodIcon = (channel: string) => {
    const channelLower = channel?.toLowerCase() || '';
    if (channelLower.includes('promptpay')) {
      return <Smartphone className="w-4 h-4 text-blue-600" />;
    }
    return <CreditCard className="w-4 h-4 text-purple-600" />;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const exportToCSV = () => {
    const headers = ['Order No', 'User', 'Email', 'Amount', 'Status', 'Method', 'Date'];
    const rows = filteredTransactions.map(txn => [
      txn.orderNo,
      txn.userName,
      txn.userEmail,
      `${txn.amount} ${txn.currency}`,
      txn.status,
      txn.channel || 'N/A',
      formatDate(txn.createdAt),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `financial-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    
    toast.success('Report exported successfully!');
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Activity className="w-12 h-12 text-green-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading financial data...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <DollarSign className="w-8 h-8 text-green-600" />
              Financial Reports
            </h1>
            <p className="text-gray-600 mt-1">View transactions and revenue reports</p>
          </div>
          <button
            onClick={exportToCSV}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>

        {/* Summary Stats */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="bg-green-500 p-2 rounded-lg">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <p className="text-sm text-green-700 mb-1">Total Revenue</p>
              <p className="text-2xl font-bold text-green-900">฿{summary.totalRevenue.toLocaleString()}</p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="bg-blue-500 p-2 rounded-lg">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <ArrowUpRight className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-sm text-blue-700 mb-1">This Month</p>
              <p className="text-2xl font-bold text-blue-900">฿{summary.monthlyRevenue.toLocaleString()}</p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <CheckCircle className="w-6 h-6 text-green-500" />
                <span className="text-xs font-semibold text-green-600">
                  {summary.totalPayments > 0 ? Math.round((summary.successfulPayments / summary.totalPayments) * 100) : 0}%
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-1">Successful</p>
              <p className="text-2xl font-bold text-gray-900">{summary.successfulPayments}</p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <Clock className="w-6 h-6 text-yellow-500" />
                <XCircle className="w-5 h-5 text-red-500" />
              </div>
              <p className="text-sm text-gray-600 mb-1">Pending / Failed</p>
              <p className="text-2xl font-bold text-gray-900">
                {summary.pendingPayments} / {summary.failedPayments}
              </p>
            </div>
          </div>
        )}

        {/* Additional Stats */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-2">
                <CreditCard className="w-5 h-5 text-purple-500" />
                <p className="text-sm text-gray-600">Total Transactions</p>
              </div>
              <p className="text-2xl font-bold text-gray-900">{summary.totalPayments}</p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-5 h-5 text-blue-500" />
                <p className="text-sm text-gray-600">Avg Transaction</p>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                ฿{Math.round(summary.averageTransactionValue).toLocaleString()}
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <p className="text-sm text-gray-600">Success Rate</p>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {summary.totalPayments > 0 
                  ? Math.round((summary.successfulPayments / summary.totalPayments) * 100)
                  : 0}%
              </p>
            </div>
          </div>
        )}

        {/* Billing queue */}
        <div className="bg-white border border-amber-200 rounded-xl overflow-hidden mb-8">
          <div className="px-6 py-4 bg-amber-50 border-b border-amber-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-amber-950">Billing queue</h2>
              <p className="text-sm text-amber-800">
                Pending provider/manual requests awaiting admin confirmation.
              </p>
            </div>
            <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold">
              {billingQueue.length} open
            </span>
          </div>
          {billingQueue.length === 0 ? (
            <div className="p-6 text-sm text-gray-500">No pending billing intents.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Product</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Invoice</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {billingQueue.map((intent) => {
                    const lineItem = intent.invoice?.line_items?.[0];
                    return (
                      <tr key={intent.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {intent.user?.name || intent.user?.email || 'Unknown'}
                          </div>
                          <div className="text-xs text-gray-500">{intent.user?.email || 'No email'}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {lineItem?.name || intent.product_id}
                          </div>
                          <div className="text-xs text-gray-500">{intent.product_type}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-bold text-gray-900">
                            ฿{Number(intent.invoice?.total_amount || intent.amount || 0).toLocaleString()}
                          </div>
                          <div className="text-xs font-mono text-gray-500">{intent.invoice?.id || 'No invoice'}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-3 py-1 bg-yellow-50 text-yellow-700 rounded-full text-xs font-semibold">
                            {intent.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => handleBillingAction(intent, 'mark-paid')}
                              className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-semibold"
                            >
                              Mark paid
                            </button>
                            <button
                              onClick={() => handleBillingAction(intent, 'mark-failed')}
                              className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg text-xs font-semibold"
                            >
                              Mark failed
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[300px] relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by user, email, or order number..."
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as any)}
                className="px-4 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="success">Success Only</option>
                <option value="pending">Pending Only</option>
                <option value="failed">Failed Only</option>
              </select>
            </div>
          </div>
        </div>

        {/* Transactions Table */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Order No</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">User</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Method</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Plan</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTransactions.map((txn) => (
                  <tr key={txn.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-mono text-gray-900">{txn.orderNo}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{txn.userName}</div>
                        <div className="text-xs text-gray-500">{txn.userEmail}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-gray-900">
                        ฿{txn.amount.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getPaymentMethodIcon(txn.channel)}
                        <span className="text-sm text-gray-600">{txn.channel || 'N/A'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-semibold uppercase">
                        {txn.subscriptionPlan || 'Pro'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(txn.status)}
                        {getStatusBadge(txn.status)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {formatDate(txn.createdAt)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredTransactions.length === 0 && (
            <div className="text-center py-12">
              <DollarSign className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No transactions found</p>
              <p className="text-gray-400 text-sm">Try adjusting your search or filters</p>
            </div>
          )}
        </div>

        {/* Footer Info */}
        <div className="mt-6 flex items-center justify-between text-sm text-gray-600">
          <p>Showing {filteredTransactions.length} of {transactions.length} transactions</p>
          <p>Last updated: {new Date().toLocaleTimeString()}</p>
        </div>
      </div>
    </AdminLayout>
  );
};
